import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import publicApi from "@/lib/publicApi";
import { Input } from "@/components/ui/input";
import { Search, MapPin, Loader2, ArrowRight } from "lucide-react";

const CONTAINER = "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8";

export const ServiceAreaSearch = ({ content }) => {
  const s = content?.service_areas || {};
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const run = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSearched(true);
    try {
      const { data } = await publicApi.get("/public/service-areas", { params: { q } });
      setResults(data.items || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="service-areas" className="py-16 sm:py-24 bg-[#9BB9CE]/20 scroll-mt-24" data-testid="service-areas-section">
      <div className={CONTAINER}>
        <div className="text-center mx-auto max-w-3xl">
          <p className="text-[14px] font-semibold uppercase tracking-[0.14em] text-[#9C1E22]">Near you</p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-display font-bold tracking-tight text-[#1C4358]">{s.title}</h2>
          <p className="mt-4 text-lg text-[#4B5563] leading-relaxed">{s.subtitle}</p>
        </div>

        <form onSubmit={run} data-testid="service-area-form" className="mt-9 mx-auto max-w-xl flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#4B5563]" />
            <Input
              data-testid="service-area-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Enter your city or area"
              className="h-14 pl-12 text-[16px] rounded-full border-[#1C4358]/20 bg-white focus-visible:ring-0"
            />
          </div>
          <button
            type="submit"
            data-testid="service-area-search-btn"
            className="mmm-tap inline-flex items-center justify-center gap-2 px-7 h-14 rounded-full text-[16px] font-semibold text-white bg-[#1C4358] hover:bg-[#163645] transition-colors"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />} Search
          </button>
        </form>

        {searched && !loading && (
          <div className="mt-8 mx-auto max-w-xl" data-testid="service-area-results">
            {results && results.length > 0 ? (
              <ul className="space-y-2.5">
                {results.slice(0, 8).map((c) => (
                  <li key={c.id} data-testid={`service-area-result-${c.id}`}
                    className="flex items-center justify-between gap-3 rounded-[0.9rem] bg-white border border-[#1C4358]/10 px-5 py-4">
                    <span className="flex items-center gap-3 text-[16px] text-[#252221]">
                      <MapPin size={18} className="text-[#9C1E22]" />
                      <span className="font-semibold text-[#1C4358]">{c.name}</span>
                      {c.metro ? <span className="text-[#4B5563]">· {c.metro}</span> : null}
                    </span>
                    <button
                      type="button"
                      onClick={() => navigate("/book", { state: { city: c.name } })}
                      className="text-[15px] font-semibold text-[#9C1E22] inline-flex items-center gap-1"
                    >
                      Get help <ArrowRight size={15} />
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="rounded-[0.9rem] bg-white border border-[#1C4358]/10 px-6 py-8 text-center">
                <p className="text-[16px] text-[#252221]">
                  We're expanding all the time — and we can still help your family.
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/book")}
                  className="mmm-tap mt-4 inline-flex items-center gap-2 px-6 h-12 rounded-full text-[15px] font-semibold text-white bg-[#9C1E22] hover:bg-[#861A1D] transition-colors"
                >
                  Talk with a guide <ArrowRight size={16} />
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};
